/**
 * <ReceivedShareRow> — single row in the Received tab list view.
 *
 * `external-sharing` Phase 4 Session 4.1 (2026-05-09).
 *
 * Columns: Sender | Deal Title | Created | Actions ([Open] + [Fork]).
 * Unread state (`recipientFirstViewedAt === null`) drives a left-border
 * accent + a [NEW] chip near the deal title. Optional sender message
 * renders as a quoted block below the row title when present.
 *
 * Distinct from `<SentShareRow>`: recipients only see active shares (Phase 1
 * RLS gates `shared_links_recipient_select` on `status='active'`), so no
 * status column. Revoke is owner-only.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/Button';
import { formatRelativeTime } from '@/lib/format';
import type { SharedLinkResponse } from '@/lib/shares-api';
import { VersionGraphModal } from '@/components/sharing';

const RECIPIENT_KIND_LABELS: Record<SharedLinkResponse['recipientKind'], string> = {
  internal: 'Internal',
  external: 'External',
};

/**
 * Grid-template-columns for the received-share row. Exported so
 * `<SharesTableHeader>` can mount above the rows with the same template
 * — headers can't drift from cells.
 *
 * Fixed widths for non-flex tracks — see SENT_SHARE_ROW_GRID for the same
 * rationale (auto-sized columns resolve to different widths in header vs
 * row, breaking alignment).
 */
export const RECEIVED_SHARE_ROW_GRID =
  'grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_100px_260px]';

export interface ReceivedShareRowProps {
  share: SharedLinkResponse;
  highlighted: boolean;
  onFork: (shareId: string) => void;
  forking: boolean;
}

export function ReceivedShareRow({
  share,
  highlighted,
  onFork,
  forking,
}: ReceivedShareRowProps) {
  const [graphOpen, setGraphOpen] = useState(false);
  const isUnread = share.recipientFirstViewedAt === null;
  const senderLabel = share.ownerName?.trim().length
    ? share.ownerName
    : 'Arcanum user';
  const dealTitle = share.conversationTitle || 'Untitled deal';

  return (
    <>
    <div
      id={`share-row-${share.id}`}
      data-testid={`share-row-${share.id}`}
      data-unread={isUnread ? 'true' : 'false'}
      className={clsx(
        RECEIVED_SHARE_ROW_GRID,
        'items-start gap-4',
        'px-4 py-3 border bg-panel-bg rounded-[3px]',
        isUnread
          ? 'border-l-2 border-l-accent border-y-panel-border border-r-panel-border'
          : 'border-panel-border',
        highlighted && 'ring-1 ring-accent border-accent/40',
      )}
    >
      <div className="min-w-0">
        <div className="text-sm font-mono text-text-bright truncate">{senderLabel}</div>
        <div className="text-xs text-text-muted font-mono uppercase tracking-[0.5px]">
          {RECIPIENT_KIND_LABELS[share.recipientKind]}
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-body text-text-bright truncate">{dealTitle}</span>
          {isUnread && (
            <span
              data-testid={`share-row-${share.id}-new-chip`}
              className="text-micro font-mono text-accent uppercase tracking-[0.5px] px-1.5 py-0.5 border border-accent/30 rounded-[2px]"
            >
              NEW
            </span>
          )}
        </div>
        {share.message && (
          <p className="text-xs italic text-text-muted mt-1 pl-2 border-l border-panel-border">
            &ldquo;{share.message}&rdquo;
          </p>
        )}
      </div>
      <span className="text-xs text-text-muted font-mono pt-0.5">
        {formatRelativeTime(share.createdAt)}
      </span>
      <div className="flex items-center gap-2">
        <Link
          to="/builder"
          search={{ share: share.id, tab: 'workbench' as const }}
          data-testid={`share-row-${share.id}-open`}
        >
          <Button
            variant="secondary"
            className="!h-8 !py-0 !px-3 inline-flex items-center justify-center whitespace-nowrap text-xs"
          >
            Open
          </Button>
        </Link>
        <Button
          variant="secondary"
          onClick={() => setGraphOpen(true)}
          data-testid={`share-row-${share.id}-version-graph`}
          className="!h-8 !py-0 !px-3 inline-flex items-center justify-center whitespace-nowrap text-xs"
          title="Open version graph"
        >
          Versions
        </Button>
        <Button
          variant="primary"
          onClick={() => onFork(share.id)}
          disabled={forking}
          data-testid={`share-row-${share.id}-fork`}
          className="!h-8 !py-0 !px-3 inline-flex items-center justify-center whitespace-nowrap text-xs"
        >
          {forking ? 'Forking…' : 'Fork'}
        </Button>
      </div>
    </div>
    <VersionGraphModal
      open={graphOpen}
      shareId={share.id}
      currentConversationId={share.conversationId}
      viewerKind="recipient"
      onClose={() => setGraphOpen(false)}
    />
    </>
  );
}
